// Bridges the backend audit-log contract to the table's display shape.

export function humanizeField(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const display = (value) => {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const toText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const firstText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return null;
};

function pickLabelFromObject(source = {}) {
  if (!source || typeof source !== 'object') return null;

  const candidateKeys = [
    'name',
    'full_name',
    'fullName',
    'label',
    'title',
    'display_name',
    'displayName',
    'action_label',
    'actionLabel',
    'role_label',
    'roleLabel',
    'purpose_label',
    'purposeLabel',
    'age_class_label',
    'ageClassLabel',
    'breed_name',
    'breedName',
    'species_name',
    'speciesName',
    'species_label',
    'speciesLabel',
    'common_name_nigeria',
    'commonNameNigeria',
    'institution_name',
    'institutionName',
    'email',
    'code',
  ];

  for (const key of candidateKeys) {
    const text = toText(source[key]);
    if (text) return text;
  }

  for (const [key, value] of Object.entries(source)) {
    if (typeof value !== 'string') continue;
    const text = toText(value);
    if (!text) continue;
    if (/(^id$|_id$|^uuid$|_uuid$|password|token|secret|created_at|updated_at|deleted_at|verified_at)$/i.test(key)) {
      continue;
    }
    return text;
  }

  return null;
}

export function deriveChanges(previousState, newState) {
  const prev = previousState || {};
  const next = newState || {};
  const keys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));

  return keys.reduce((acc, key) => {
    const before = prev[key];
    const after = next[key];
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      acc.push({ field: humanizeField(key), previous: display(before), next: display(after) });
    }
    return acc;
  }, []);
}

function resolveAuditActorName(row = {}) {
  return firstText(row.actor_name, row.actor?.name, row.actor?.full_name, row.actor?.fullName, 'System');
}

function resolveAuditActorRole(row = {}) {
  return firstText(
    row.actor_role,
    row.actor?.role,
    row.actor?.title,
    row.context?.device_name,
    row.context?.deviceName,
    row.context?.scope,
    row.ip_address,
    row.endpoint,
    'System'
  );
}

function resolveAuditActionLabel(row = {}) {
  return firstText(row.action_label, row.actionLabel, row.action, 'Action');
}

function resolveAuditTargetType(row = {}) {
  return firstText(row.target_type, row.target?.type, row.target?.label ? humanizeField(row.target.label) : null, 'Record');
}

function resolveAuditTargetLabel(row = {}) {
  const explicit = firstText(row.target_label, row.targetLabel);
  if (explicit) return explicit;

  const stateLabel = pickLabelFromObject(row.new_state) || pickLabelFromObject(row.previous_state);
  if (stateLabel) return stateLabel;

  const actionCode = String(row.action || '').toUpperCase();
  if (actionCode === 'LOGIN' || actionCode === 'LOGOUT') {
    return firstText(
      row.endpoint,
      row.context?.device_name,
      row.context?.deviceName,
      row.ip_address,
      row.target?.type ? humanizeField(row.target.type) : null,
      'Authentication'
    );
  }

  const targetType = firstText(row.target?.type, row.target_type);
  if (targetType) return humanizeField(targetType);

  return firstText(row.endpoint, row.uuid, 'Audit entry');
}

function resolveTimestamp(row = {}) {
  return firstText(row.timestamp, row.created_at, row.createdAt);
}

export function normalizeAuditRow(row = {}) {
  const changes = Array.isArray(row.changes) ? row.changes : deriveChanges(row.previous_state, row.new_state);

  return {
    id: row.uuid ?? row.id ?? null,
    uuid: row.uuid ?? row.id ?? null,
    timestamp: resolveTimestamp(row),
    actor_name: resolveAuditActorName(row),
    actor_role: resolveAuditActorRole(row),
    action: firstText(row.action, row.actionType, row.action_type) ?? '',
    action_label: resolveAuditActionLabel(row),
    target_label: resolveAuditTargetLabel(row),
    target_type: resolveAuditTargetType(row),
    target_uuid: row.target?.uuid ?? row.target_uuid ?? null,
    ip_address: row.ip_address ?? null,
    endpoint: row.endpoint ?? null,
    context: row.context ?? null,
    previous_state: row.previous_state ?? null,
    new_state: row.new_state ?? null,
    changes,
  };
}
